import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { openDialog } from 'src/redux/actions/dialogActions';
import { getNotificationsAction } from 'src/redux/actions/notificationActions';
import { DIALOGS } from 'src/models/DialogModel';
import { useEffect, useState } from 'react';
import { t } from 'src/lib/language';
import {
  NotificationContact, NotificationType, NOTIFICATION_TYPES,
} from 'src/models/NotificationModel';
import EditNotificationContactForm from 'src/components/Forms/EditNotificationContactForm';
import AddNotificationContactForm from 'src/components/Forms/AddNotificationContactForm';
import Item from 'src/components/Elements/Item';
import EditButton from 'src/components/UI/EditButton';
import DeleteButton from 'src/components/Elements/DeleteButton';
import ItemSearchBox from 'src/components/Elements/ItemSearchBox';
import Scroller from 'src/components/UI/Scroller';
import FormSingleSelect from 'src/components/Control/FormControls/FormSingleSelect';
import { FORM } from 'src/constants/Form';

export enum STATE {
  list = 'list',
  edit = 'edit',
  add = 'add',
}

const ManageNotifications = () => {
  const dispatch = useAppDispatch();
  const notifications = useSelector((state: Store) => state.notifications);
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const [currentContact, setCurrentContact] = useState({} as NotificationContact);
  const [currentState, setCurrentState] = useState(STATE.list);
  const [currentFilter, setCurrentFilter] = useState('');
  const [currentNotificationType, setCurrentNotificationType] = useState<NotificationType>(
    NOTIFICATION_TYPES.failedPaymentsReport,
  );

  const notification = notifications.find((item) => item.type === currentNotificationType);
  const notificationTypeItems = [
    {
      id: NOTIFICATION_TYPES.failedPaymentsReport,
      text: t(lang, 'forms.notifications.failed_payments_report'),
    },
    {
      id: NOTIFICATION_TYPES.paidPaymentsReport,
      text: t(lang, 'forms.notifications.paid_payments_report'),
    },
  ];

  useEffect(() => {
    dispatch(getNotificationsAction());
  }, []);

  const handleNotificationTypeChange = (notificationType: NotificationType) => {
    if (!notificationType) {
      return;
    }
    setCurrentNotificationType(notificationType);
    setCurrentState(STATE.list);
    setCurrentFilter('');
  };

  const handleDelete = (contact: NotificationContact) => {
    dispatch(openDialog({
      dialog: DIALOGS.deleteNotificationContact,
      payload: { notification, contact },
    }));
  };

  const handleEdit = (contact: NotificationContact) => {
    setCurrentContact(contact);
    setCurrentState(STATE.edit);
  };

  const handleAdd = () => {
    setCurrentContact({} as NotificationContact);
    setCurrentState(STATE.add);
  };

  const displayContact = (contact: NotificationContact) => {
    if (contact.email && contact.phone) return `${contact.email} — ${contact.phone}`;
    return contact.email || contact.phone || '';
  };

  const listItems = () => {
    const contacts = notification?.contacts ?? [];
    const list = contacts.map((contact: NotificationContact) => {
      const display = displayContact(contact);
      if (currentFilter && !display.includes(currentFilter)) return null;
      return (
        <Item key={`notification-contact-${contact.id}`}>
          <span><b>{display}</b></span>
          <EditButton
            onClick={() => handleEdit(contact)}
            title={t(lang, 'menus.edit_notification_contact')} />
          <DeleteButton
            onClick={() => handleDelete(contact)}
            title={t(lang, 'menus.delete_notification_contact')} />
        </Item>
      );
    });
    return (<>{list}</>);
  };

  const editForm = () => (
    notification && (
      <EditNotificationContactForm
        notification={notification}
        contact={currentContact}
        onClose={() => setCurrentState(STATE.list)} />
    )
  );

  const addForm = () => (
    <AddNotificationContactForm
      notification={notification ?? null}
      type={currentNotificationType}
      onClose={() => setCurrentState(STATE.list)} />
  );

  return (
    <>
      { currentState === STATE.list && (
        <div style={{ paddingTop: '16px' }}>
          <FormSingleSelect
            form={FORM.get_notifications}
            id="notification-report-type"
            items={notificationTypeItems}
            label={t(lang, 'forms.notifications.report_type')}
            name="notification_type"
            onChange={handleNotificationTypeChange}
            value={currentNotificationType}
            noMarginTop
            style={{ width: '320px' }} />
        </div>
      )}
      { currentState === STATE.list && (
        <ItemSearchBox
          onAdd={handleAdd}
          title={t(lang, 'misc.add')}
          label={t(lang, 'forms.notifications.search_contacts')}
          onChange={setCurrentFilter} />
      )}
      <Scroller height="697px">
        { currentState === STATE.list && listItems() }
        { currentState === STATE.add && addForm() }
        { currentState === STATE.edit && editForm() }
      </Scroller>
    </>
  );
};

export default ManageNotifications;
